/**
 * THE WITHHOLDING CONTRACT.
 *
 * The API withholds facts from callers who may not be told them. It does this
 * in EIGHT different notations, and a client that wants to render "you were not
 * shown this" correctly currently needs a branch for each. This module is the
 * one place that knows all eight, so every screen needs one.
 *
 * The prose version — when each notation applies, why there are three HTTP
 * status codes, and which surfaces do not yet conform — is
 * `docs/withholding-contract.md`. This file is the part a client executes.
 *
 * ---------------------------------------------------------------------------
 * THE ONE RULE UNDERNEATH ALL OF IT
 * ---------------------------------------------------------------------------
 *
 * A withheld fact must never render as an absence.
 *
 * "Not set", "None", "—", an empty list, a zero: each of those is an
 * AFFIRMATIVE STATEMENT ABOUT THE WORLD. Saying one of them about a fact that
 * exists and is merely being kept from this reader is a lie, and it is the
 * particular kind of lie this codebase ranks worst — something that looks like
 * an answer and is not. A program whose lead is withheld is not an unled
 * program. A roster whose names are withheld is not a roster of anonymous
 * people with no supervisor. An empty actor filter is not an organization where
 * nobody has done anything.
 *
 * Every notation below exists to let a client tell those two apart. The reason
 * there are eight is history, not design.
 *
 * ---------------------------------------------------------------------------
 * THE EIGHT NOTATIONS, AND WHICH TO REACH FOR
 * ---------------------------------------------------------------------------
 *
 * A. FLAG BESIDE A NULLED FIELD — `programLeadNameWithheld`, `labelWithheld`,
 *    `TimesheetSnapshot.rowsWithheld`.
 *    The field is `null`; a sibling boolean says whether that null is a
 *    withholding. The flag is TRUE ONLY WHEN THERE WAS A VALUE, so `false` does
 *    not mean "you may see it" — it means "nothing was kept back here".
 *    Read with `fromFlag`.
 *
 * B. FLAG BESIDE A NULLED ROW — `identityWithheld`, `detailRestricted`,
 *    `WeekShift.session.punchFactsWithheld`,
 *    `RejectedCandidate.provenanceWithheld`,
 *    `ContributingRule.provenanceWithheld`.
 *    Same notation, wider blast radius: several fields on one row are nulled
 *    together and one flag covers all of them. Unlike (A) these are true
 *    whenever the CALLER is unentitled, whether or not each individual field
 *    had a value. Read with `fromFlag`.
 *
 *    `punchFactsWithheld` is (B) rather than (A) for a reason worth keeping:
 *    two of the three fields it covers are LEGITIMATELY null on a full row (an
 *    open session has no clock-out and no computed minutes), so a per-field
 *    "was there a value" flag would report `false` for them while the caller
 *    was in fact being told nothing.
 *
 *    `provenanceWithheld` is (B) for the same reason and covers a whole NESTED
 *    OBJECT rather than sibling fields: `publisher`, `citation`, `referenceUrl`
 *    and `jurisdiction` are all legitimately absent on a complete `Provenance`.
 *    Its cause differs from the others' — the caller may not read the DOCUMENT
 *    the rule came out of, applicable or rejected alike — but the remedy is a
 *    permission like theirs, so it words the same. Nothing but the document's
 *    identity is ever withheld by it: a REJECTION keeps its `id`, `subject` and
 *    `reasons`, because "we considered this and here is the dimension it failed
 *    on" is the answer that array exists to give, and a CONTRIBUTING rule keeps
 *    its `statement`, `requirement` and `role`, because an operator must be told
 *    the rule they are held to.
 *
 * C. FLAG BESIDE AN EMPTIED COLLECTION — `supervisorsWithheld`,
 *    `actorsWithheld`. The array arrives `[]`. Without the flag an emptied list
 *    and an empty one are the same response. Read with `fromFlag`.
 *
 * D. ENUM MEMBER — `AuditActor.kind === 'withheld'`. The withholding is a
 *    VARIANT of the value rather than a flag beside it. Deliberately distinct
 *    from `'removed'`: "A removed member" asserts the account is gone, which is
 *    a different and false claim. Read with `fromActorKind`.
 *
 * E. PROSE SENTINEL — `AuditActor.label === 'A member whose name is withheld'`.
 *    The field that normally carries a real name carries a sentence instead, so
 *    a row summary reads correctly with no client logic at all. Convenient, and
 *    the one notation a client must NEVER pattern-match on: it is display copy,
 *    it is not stable, and (D) carries the same information as data.
 *
 * F. DROPPED KEY NAMED IN AN ENVELOPE — `AnalyticsDisclosure.withheld: string[]`.
 *    The key is absent from the payload entirely and a separate envelope lists
 *    what was dropped. The only notation where the value's own key tells you
 *    nothing. Read with `fromEnvelope`.
 *
 * G. SUPPRESSION, WHICH IS NOT WITHHOLDING — `AnalyticsSuppression`. A figure
 *    is hidden because too FEW PEOPLE stand behind it, not because of who is
 *    asking. Different cause, different sentence, and it is not this module's
 *    business: `apps/admin/.../analytics/disclosure.ts` owns it.
 *
 * G2. SCOPE, WHICH IS ALSO NOT WITHHOLDING — `scoped: boolean` on
 *    `CredentialOverview`, `SupervisionOverview`, the two `WorkersPage`s,
 *    `DocumentationListResponse`, and `AnalyticsFilterOptions`.
 *
 *    Nothing is being kept back. The figure is COMPLETE for the population it
 *    counts; what changed is the DENOMINATOR. `expired: 0` read by a supervisor
 *    with two compliant reports is a true claim about their two people and a
 *    false one about the organization, so what the reader needs is which
 *    population was counted, not a redaction notice.
 *
 *    That is why it is spelled `scoped` and not `somethingWithheld`, and why
 *    `scoped: true` is NOT "the opposite polarity" of `withheld: true` — both
 *    mean "this happened". They are different predicates about different
 *    things. Use `scopeSentence` so two screens cannot word it differently.
 *
 * G3. NOT CALCULABLE, WHICH IS ALSO NOT WITHHOLDING —
 *    `dashboard.OrgUtilization` and `dashboard.WorkerPlanDenominator`, both
 *    carrying `kind: 'not_calculable'` with a `NotCalculableReason`.
 *
 *    THERE IS NO FIGURE, FOR ANYBODY. Nobody has recorded this worker's
 *    contracted weekly hours, or somebody recorded zero, or the range is longer
 *    than proration is honest over. Every reader gets the same answer and the
 *    remedy is DATA ENTRY, never a permission.
 *
 *    Do not read it through this module. Saying "Not shown to you" about a
 *    column nobody ever filled in sends a supervisor to ask for access they
 *    already hold; the mirror mistake tells a compliance reader the
 *    organization has no data when it has data they may not see. That is this
 *    module's own withheld/absent collapse, one level up.
 *
 *    Authorization and calculability can BOTH be true of one response and they
 *    travel in different fields — `scope` and the route's 403 for the first,
 *    `kind` for the second — so a client never infers one from the other.
 *    Render the server's `message`; it is written for this exact reason.
 *
 * H. HTTP STATUS. Three, and they are three different questions:
 *      200 + a notation above — you may have this response; a fact inside it
 *                               is kept back, and the response says so.
 *      403                    — you may not perform this operation, or you may
 *                               not WRITE a field you were not shown.
 *      404                    — the existence of this resource is itself not
 *                               yours to learn (a scoped reader, an out-of-scope
 *                               program). Never 403 here: a 403 confirms the
 *                               row exists.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT A RENAME
 * ---------------------------------------------------------------------------
 *
 * Collapsing `identityWithheld`, `programLeadNameWithheld`, `labelWithheld`,
 * `actorsWithheld`, `supervisorsWithheld` and `detailRestricted` into one
 * spelling would touch every domain in the API at once. That is a bigger and
 * riskier change than reading them consistently, and it would not by itself
 * stop the next surface inventing a seventh name. What stops that is a written
 * contract with a reader beside it — this module — plus the rule that a new
 * withholding signal must be added HERE before a screen consumes it.
 */
/**
 * The words every withheld value uses.
 *
 * ONE phrase across the product, because a person who meets "Not shown to you"
 * on the audit log and "Restricted" on a program has to learn twice that they
 * mean the same thing. It is second person on purpose: the fact is not missing
 * and it is not secret in general — it is not being shown TO YOU, which is a
 * statement about a permission somebody can go and change.
 *
 * This is the phrase `packages/api-client/src/programs.ts` already instructs
 * clients to render ("Render 'not shown to you'"), and the head phrase
 * `apps/admin/src/features/mileage/Mileage.tsx` already uses ("Not shown — …").
 */
export const WITHHELD_LABEL = 'Not shown to you';
/** The longer form, for somewhere with room for a reason. */
export function withheldSentence(reason) {
    return `${WITHHELD_LABEL} — ${reason}`;
}
/**
 * The sentence for a figure counted over the caller's scope (notation G2).
 *
 * Second person, like `WITHHELD_LABEL`, and for the same reason: the number is
 * not wrong and it is not secret — it counts what YOU can reach, which is a
 * statement about a grant somebody can go and change.
 */
export function scopeSentence(population) {
    return `These figures cover ${population} — not the whole organization. Someone with wider access may see more.`;
}
/** The reasons, so two screens withholding the same fact cannot word it differently. */
export const WITHHELD_BECAUSE = {
    /**
     * Everything governed by `user:read`: a worker's name, job title, employment
     * status, who they report to, and a program lead — which is a worker's name
     * reached through a join.
     */
    workerIdentity: 'naming a member of your organization needs the directory permission',
    /** The audit log's actor and target halves, governed by the same permission. */
    auditSubject: 'you do not have permission to see who this was',
    /** A worker's own un-tagged drive. Not a permission — the worker's choice. */
    personalTrip: 'the worker has not marked this as work',
    /**
     * WHICH records an import left behind, governed by `integrations:manage`
     * rather than the `integrations:read` the rest of that screen takes. Every
     * other integrations read is connection HEALTH; an entry here names a
     * specific client encounter in the customer's EHR by the source's own record
     * id, org-wide and unscoped.
     */
    rejectedRecordIdentity: 'naming the records a source system sent needs the org-admin integrations permission',
    /**
     * A ROLE-QUEUE TASK WHOSE UNDERLYING OBJECT THIS READER CANNOT REACH.
     *
     * Being a member of the role a task was routed to makes the task VISIBLE; it
     * is not authority over the client, worker or other protected record the work
     * is about. That authority is the canonical one — an open caseload
     * assignment, or `directory:read_all` — and it is asked independently.
     *
     * Second person, and it names the remedy rather than the subject: the
     * sentence is equally true of a participant on somebody else's caseload and
     * of one who does not exist, which is what stops it being an existence
     * oracle.
     */
    taskObjectDetail: 'this task was routed to a role you hold, and its detail needs access to the record the work is about',
};
const shown = (value) => ({ state: 'shown', value, placeholder: '' });
const withheld = (reason) => ({
    state: 'withheld',
    value: null,
    placeholder: withheldSentence(reason),
});
const absent = (placeholder) => ({
    state: 'absent',
    value: null,
    placeholder,
});
/**
 * NOTATIONS A, B and C: a value (or list) beside a boolean flag.
 *
 * `absentLabel` is what to say when the value is genuinely missing — "Not set",
 * "No fixed site", "—". It is REQUIRED rather than defaulted, because choosing
 * it is the moment a caller has to notice that an absence and a withholding are
 * two different sentences.
 *
 * ORDER MATTERS. The flag is consulted FIRST. A withheld field arrives null, so
 * a null-check that runs first swallows the withholding and prints the absence
 * label — which is exactly the bug this contract exists to stop.
 */
export function fromFlag(value, isWithheld, absentLabel, reason = WITHHELD_BECAUSE.workerIdentity) {
    if (isWithheld)
        return withheld(reason);
    if (value == null)
        return absent(absentLabel);
    return shown(value);
}
/**
 * NOTATION C, for a list. An emptied collection and an empty one are the same
 * `[]`, so the flag is the only thing that separates them.
 */
export function listFromFlag(items, isWithheld, absentLabel, reason = WITHHELD_BECAUSE.workerIdentity) {
    if (isWithheld)
        return withheld(reason);
    if (items == null || items.length === 0)
        return absent(absentLabel);
    return shown(items);
}
/**
 * NOTATION D: the withholding arrived as a variant of the value.
 *
 * `'withheld'` and `'removed'` are the two that a client must not conflate.
 * `'removed'` asserts the account no longer exists — a claim about the world.
 * `'withheld'` asserts only that this reader may not be told the name.
 */
export function fromActorKind(kind) {
    return kind === 'withheld' ? 'withheld' : 'shown';
}
/**
 * NOTATION F: the key is gone, and a separate envelope names what went.
 *
 * The only notation where the value's own absence carries no information, so
 * the envelope has to be consulted before the payload is read.
 */
export function fromEnvelope(value, envelope, field, absentLabel, reason = WITHHELD_BECAUSE.workerIdentity) {
    if (envelope?.withheld.includes(field) ?? false)
        return withheld(reason);
    if (value == null)
        return absent(absentLabel);
    return shown(value);
}
/**
 * The single line a screen renders for a value it may or may not have been
 * shown. `format` is applied only in the `shown` case, so a formatter can never
 * be handed a placeholder to format.
 */
export function disclosedText(d, format) {
    return d.state === 'shown' && d.value != null ? format(d.value) : d.placeholder;
}
//# sourceMappingURL=withholding.js.map